import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { useAuthStore } from '../../store/auth-store';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import {
  CrudDrawer,
  TextField,
  TextAreaField,
  NumberField,
  CheckField,
  DeleteButton,
  extractErr,
} from '../../components/crud-drawer';

// One screen for every admin-managed vocabulary — vehicle types, KYC document
// types, rejection reasons, support categories and the rest. They all share the
// same (code, label, order, active) shape, so they share one page rather than
// seventeen near-identical ones.
//
// Wired to /admin/masters + /admin/masters/types.

interface MasterType {
  type: string;
  title: string;
  description: string;
  storedIn: string;
  group: 'catalog' | 'seller' | 'delivery' | 'support' | 'customer' | 'reason';
}

interface Lookup {
  id: string;
  type: string;
  code: string;
  label: string;
  description: string | null;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
  metadata: Record<string, unknown>;
}

const GROUP_LABEL: Record<MasterType['group'], string> = {
  catalog: 'Catalog',
  seller: 'Sellers',
  delivery: 'Delivery',
  support: 'Support',
  customer: 'Customers',
  reason: 'Reasons',
};

const GROUP_ORDER: MasterType['group'][] = ['catalog', 'seller', 'delivery', 'support', 'customer', 'reason'];

/** Known metadata flags, so the drawer offers checkboxes instead of raw JSON.
 *  Anything else on a row is preserved untouched on save. */
const META_FLAGS: { key: string; label: string; hint: string; types: string[] }[] = [
  {
    key: 'required',
    label: 'Mandatory document',
    hint: 'Submission is blocked until this document is uploaded.',
    types: ['SELLER_DOC_TYPE', 'DELIVERY_DOC_TYPE'],
  },
  {
    key: 'requiresNote',
    label: 'Requires a written note',
    hint: 'The admin or partner must add free-text detail alongside this reason.',
    types: ['PRODUCT_REJECT_REASON', 'SELLER_REJECT_REASON', 'KYC_REJECT_REASON', 'PAYOUT_REJECT_REASON', 'ORDER_CANCEL_REASON', 'DELIVERY_FAILURE_REASON'],
  },
  {
    key: 'manual',
    label: 'Selectable by a human',
    hint: 'Off means the order pipeline writes this reason automatically and nobody picks it.',
    types: ['INVENTORY_REASON'],
  },
];

const EMPTY_FORM = {
  code: '',
  label: '',
  description: '',
  sortOrder: '',
  isActive: true,
  flags: {} as Record<string, boolean>,
};

export function MastersPage() {
  const hasScope = useAuthStore((s) => s.hasScope);
  const canWrite = hasScope('master.write');

  const [types, setTypes] = useState<MasterType[]>([]);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [rows, setRows] = useState<Lookup[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(true);

  const [editing, setEditing] = useState<Lookup | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const { confirm } = useConfirm();

  const meta = types.find((t) => t.type === activeType) ?? null;

  useEffect(() => {
    void (async () => {
      try {
        const { data } = await api.get<MasterType[]>('/admin/masters/types');
        setTypes(data);
        setActiveType((cur) => cur ?? data[0]?.type ?? null);
      } catch (err) {
        toast.error(extractErr(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const loadRows = async (type: string) => {
    setRowsLoading(true);
    try {
      const { data } = await api.get<Lookup[]>('/admin/masters', { params: { type } });
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    if (activeType) void loadRows(activeType);
  }, [activeType]);

  const grouped = useMemo(() => {
    const map = new Map<MasterType['group'], MasterType[]>();
    for (const t of types) {
      const list = map.get(t.group) ?? [];
      list.push(t);
      map.set(t.group, list);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ group: g, items: map.get(g)! }));
  }, [types]);

  const flagsForType = META_FLAGS.filter((f) => activeType && f.types.includes(activeType));
  const shownRows = showInactive ? rows : rows.filter((r) => r.isActive);
  // Client-side: the whole vocabulary is loaded. Matches every column — label,
  // description, code, System badge, flags, order and Active.
  const { query, setQuery, filtered: visibleRows } = useTableSearch(shownRows, (r) => [
    r.label,
    r.description,
    r.code,
    r.isSystem ? 'System' : '',
    flagsForType.filter((f) => r.metadata?.[f.key] === true).map((f) => f.label),
    r.sortOrder,
    r.isActive ? 'Active' : 'Inactive deactivated',
  ]);
  // A search is for one list; switching lists starts afresh.
  useEffect(() => { setQuery(''); }, [activeType, setQuery]);
  const searching = query.trim().length > 0;

  // ---- Form plumbing --------------------------------------------------------

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    setForm({ ...EMPTY_FORM, flags: {} });
  };

  const openEdit = (row: Lookup) => {
    setCreating(false);
    setEditing(row);
    setForm({
      code: row.code,
      label: row.label,
      description: row.description ?? '',
      sortOrder: String(row.sortOrder),
      isActive: row.isActive,
      flags: Object.fromEntries(
        flagsForType.map((f) => [f.key, (row.metadata?.[f.key] as boolean | undefined) ?? false]),
      ),
    });
  };

  const closeDrawer = () => {
    setEditing(null);
    setCreating(false);
  };

  /** Merge the flag checkboxes back over whatever else the row's metadata held,
   *  so a key this UI doesn't know about survives an edit. */
  const mergedMetadata = (base: Record<string, unknown>) => {
    const out = { ...base };
    for (const f of flagsForType) out[f.key] = form.flags[f.key] ?? false;
    return out;
  };

  /** Deactivating hides an option from every new picker — ask first. */
  const confirmDeactivate = (label: string) =>
    confirm({
      title: `Deactivate “${label}”?`,
      description: 'It disappears from new pickers in the apps. Records that already use it keep showing this label. You can reactivate it at any time.',
      confirmLabel: 'Deactivate',
      tone: 'danger',
    });

  const save = async () => {
    if (!activeType) return;
    if (!form.label.trim()) {
      toast.error('Label is required');
      return;
    }
    if (creating && !form.code.trim()) {
      toast.error('Code is required');
      return;
    }
    if (editing && editing.isActive && !form.isActive && !(await confirmDeactivate(editing.label))) return;
    setBusy(true);
    try {
      if (creating) {
        await api.post('/admin/masters', {
          type: activeType,
          code: form.code.trim().toUpperCase(),
          label: form.label.trim(),
          description: form.description.trim() || null,
          ...(form.sortOrder !== '' ? { sortOrder: Number(form.sortOrder) } : {}),
          isActive: form.isActive,
          metadata: mergedMetadata({}),
        });
        toast.success('Option added');
      } else if (editing) {
        await api.put(`/admin/masters/${editing.id}`, {
          label: form.label.trim(),
          description: form.description.trim() || null,
          ...(form.sortOrder !== '' ? { sortOrder: Number(form.sortOrder) } : {}),
          isActive: form.isActive,
          metadata: mergedMetadata(editing.metadata ?? {}),
        });
        toast.success('Option updated');
      }
      closeDrawer();
      await loadRows(activeType);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing || !activeType) return;
    const ok = await confirm({
      title: `Delete “${editing.label}”?`,
      description: `Existing records that reference ${editing.code} will show the raw code instead. This can't be undone — deactivating is usually what you want.`,
      confirmLabel: 'Delete option',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/masters/${editing.id}`);
      toast.success('Option deleted');
      closeDrawer();
      await loadRows(activeType);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  /** Row-level active switch — the common edit, so it doesn't need the drawer. */
  const toggleActive = async (row: Lookup, next: boolean) => {
    if (!next && !(await confirmDeactivate(row.label))) return;
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await api.put(`/admin/masters/${row.id}`, { isActive: next });
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: !next } : r)));
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Master data" />
        <Card padding="none"><SkeletonRows cols={4} /></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Master data"
        description="The dropdowns and chip lists the apps render. Editing a label here changes it everywhere without an app release."
      />

      <div className="grid gap-6 lg:grid-cols-[16rem_minmax(0,1fr)]">
        {/* Vocabulary selector. */}
        <Card padding="none" className="h-fit">
          <nav className="max-h-[70vh] overflow-y-auto py-2">
            {grouped.map(({ group, items }) => (
              <div key={group} className="mb-2">
                <div className="px-4 py-1.5 text-[12.5px] font-semibold text-ink-500">
                  {GROUP_LABEL[group]}
                </div>
                {items.map((t) => (
                  <button
                    key={t.type}
                    type="button"
                    onClick={() => setActiveType(t.type)}
                    aria-current={activeType === t.type ? 'true' : undefined}
                    className={`flex w-full items-center gap-2 border-l-2 px-4 py-2 text-left text-[14px] transition-colors ${
                      activeType === t.type
                        ? 'border-admin bg-admin-soft font-semibold text-admin-ink'
                        : 'border-transparent text-ink-700 hover:bg-surface-sunk hover:text-ink-900'
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate">{t.title}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </Card>

        {/* Options for the selected vocabulary. */}
        <Card padding="none">
          {meta && (
            <div className="border-b border-surface-line px-5 py-4">
              <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h2 className="text-[17px] font-semibold text-ink-900">{meta.title}</h2>
                  <p className="mt-0.5 text-[13.5px] text-ink-500">{meta.description}</p>
                  <p className="sku mt-1 text-ink-400">
                    stored in {meta.storedIn}
                  </p>
                </div>
                {canWrite && (
                  <button
                    type="button"
                    onClick={openCreate}
                    className="btn btn-primary btn-sm shrink-0"
                  >
                    <Symbol name="add" size={16} /> Add option
                  </button>
                )}
              </div>
              <label className="mt-3 flex w-fit cursor-pointer items-center gap-2 text-[13.5px] text-ink-700">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="check"
                />
                Show deactivated options
              </label>
              <TableSearch
                className="mt-3"
                value={query}
                onChange={setQuery}
                placeholder={`Search ${meta.title.toLowerCase()} by label, code, flag or status`}
                resultCount={searching ? visibleRows.length : undefined}
              />
            </div>
          )}

          {rowsLoading ? (
            <SkeletonRows cols={4} />
          ) : visibleRows.length === 0 && searching && shownRows.length > 0 ? (
            <EmptyState
              bare
              icon="search_off"
              title={`No matches for “${query.trim()}”`}
              body="Try another word, or clear the search to see every option in this list."
              action={<button type="button" onClick={() => setQuery('')} className="btn btn-secondary">Clear search</button>}
            />
          ) : visibleRows.length === 0 ? (
            <EmptyState
              bare
              icon="list"
              title={rows.length > 0 ? 'Every option here is deactivated' : 'No options in this list yet'}
              body={rows.length > 0 ? 'Tick “Show deactivated options” to see them.' : canWrite ? 'Add the first option and it shows up in the apps straight away.' : undefined}
            />
          ) : (
            <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Code</th>
                  <th className="hidden md:table-cell">Flags</th>
                  <th className="text-right">Order</th>
                  <th className="text-right">Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r) => (
                  <tr key={r.id} className={r.isActive ? undefined : 'bg-surface-sunk/60'}>
                    <td>
                      <div className={`font-medium ${r.isActive ? 'text-ink-900' : 'text-ink-500'}`}>{r.label}</div>
                      {r.description && (
                        <div className="mt-0.5 text-[12.5px] text-ink-500">{r.description}</div>
                      )}
                    </td>
                    <td>
                      <span className="sku rounded-xs bg-surface-2 px-1.5 py-0.5 text-ink-700">
                        {r.code}
                      </span>
                      {r.isSystem && (
                        <span
                          title="Built in — can be relabelled or deactivated, but not deleted."
                          className="badge badge-info ml-1.5"
                        >
                          System
                        </span>
                      )}
                    </td>
                    <td className="hidden md:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {flagsForType
                          .filter((f) => r.metadata?.[f.key] === true)
                          .map((f) => (
                            <span key={f.key} className="badge badge-accent">
                              {f.label}
                            </span>
                          ))}
                      </div>
                    </td>
                    <td className="text-right font-tabular text-ink-500">{r.sortOrder}</td>
                    <td>
                      <div className="flex justify-end">
                        <Toggle
                          checked={r.isActive}
                          disabled={!canWrite}
                          aria-label={`${r.isActive ? 'Deactivate' : 'Activate'} ${r.label}`}
                          onChange={(v) => void toggleActive(r, v)}
                        />
                      </div>
                    </td>
                    <td className="text-right">
                      {canWrite && (
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="btn btn-ghost btn-sm"
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </Card>
      </div>

      <CrudDrawer
        open={creating || editing !== null}
        title={creating ? `Add to ${meta?.title ?? 'list'}` : `Edit ${editing?.label ?? ''}`}
        subtitle={meta?.description}
        busy={busy}
        onClose={closeDrawer}
        onSubmit={() => void save()}
        secondaryAction={
          editing && !editing.isSystem ? <DeleteButton onClick={() => void remove()} disabled={busy} /> : undefined
        }
      >
        <TextField
          label="Code"
          value={form.code}
          onChange={(v) => setForm((f) => ({ ...f, code: v.toUpperCase() }))}
          placeholder="THREE_WHEELER"
          required
          mono
          // Editing a code would orphan every record that already stored the old
          // spelling, so the API refuses it and the field is locked to match.
          disabled={!creating}
          hint={
            creating
              ? 'Stored in the database. A–Z, 0–9 and underscores. Cannot be changed later.'
              : 'Codes are permanent — existing records store this value.'
          }
        />
        <TextField
          label="Label"
          value={form.label}
          onChange={(v) => setForm((f) => ({ ...f, label: v }))}
          placeholder="Three-wheeler"
          required
          hint="What the apps show. Safe to change at any time."
        />
        <TextAreaField
          label="Description"
          value={form.description}
          onChange={(v) => setForm((f) => ({ ...f, description: v }))}
          placeholder="Optional helper text shown under the option"
          rows={2}
        />
        <NumberField
          label="Sort order"
          value={form.sortOrder}
          onChange={(v) => setForm((f) => ({ ...f, sortOrder: v }))}
          placeholder="Leave blank to add at the end"
          min={0}
          hint="Lower numbers appear first."
        />

        {flagsForType.map((f) => (
          <CheckField
            key={f.key}
            label={f.label}
            hint={f.hint}
            checked={form.flags[f.key] ?? false}
            onChange={(v) => setForm((s) => ({ ...s, flags: { ...s.flags, [f.key]: v } }))}
          />
        ))}

        <CheckField
          label="Active"
          hint="Deactivated options disappear from new pickers, but records that already use them keep resolving to this label."
          checked={form.isActive}
          onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
        />
      </CrudDrawer>
    </div>
  );
}
